// src/pages/pedidos/RelatorioVendasGarcomPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Input from '../../components/input/Input';
import PageContainer from '../../components/geral/PageContainer';
import PageContent from '../../components/geral/PageContent';
import PageFooter from '../../components/geral/PageFooter';
import PageHeader from '../../components/geral/PageHeader';
import { useMessage } from '../../contexts/geral/MessageContext';
import { useUsuario } from '../../contexts/geral/UsuarioContext';
import { getRelatorioVendasGarcom } from '../../services/pedidos/PedidoService';
import { formatDateInput } from '../../utils/dateTimeUtils';
import { formatCurrency, formatNumeric } from '../../utils/numberUtils';
import './RelatorioVendasGarcomPage.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const relatorioVazio = {
  produtos: [],
  pedidos: [],
  vendasPorGarcom: [],
  totais: {
    totalItens: 0,
    quantidadeItens: 0,
    quantidadePedidos: 0,
    valorTotal: 0,
  },
};

const RelatorioVendasGarcomPage = () => {
  const { showMessage } = useMessage();
  const { showUsuarioSearch } = useUsuario();
  const [dataIni, setDataIni] = useState('');
  const [horaIni, setHoraIni] = useState('00:00');
  const [dataFim, setDataFim] = useState('');
  const [horaFim, setHoraFim] = useState('23:59');
  const [usuario, setUsuario] = useState(null);
  const [relatorio, setRelatorio] = useState(relatorioVazio);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => {
    const hoje = new Date();
    const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

    setDataIni(formatDateInput(primeiroDiaMes));
    setDataFim(formatDateInput(hoje));
  }, []);

  const totais = useMemo(() => relatorio?.totais || relatorioVazio.totais, [relatorio]);

  const vendasPorGarcom = useMemo(
    () => relatorio?.vendasPorGarcom || relatorioVazio.vendasPorGarcom,
    [relatorio],
  );

  const graficoVendasGarcomData = useMemo(() => ({
    labels: vendasPorGarcom.map((item) => item.nomeUsuario || 'Sem usuário'),
    datasets: [
      {
        label: 'Valor vendido',
        data: vendasPorGarcom.map((item) => item.valorTotal || 0),
        backgroundColor: '#198754',
        borderRadius: 4,
        maxBarThickness: 52,
      },
    ],
  }), [vendasPorGarcom]);

  const graficoVendasGarcomOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => `Valor vendido: ${formatCurrency(context.raw || 0)}`,
          afterLabel: (context) => {
            const garcom = vendasPorGarcom[context.dataIndex];
            return `Pedidos: ${garcom?.quantidadePedidos || 0}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatCurrency(value),
        },
      },
    },
  }), [vendasPorGarcom]);

  const selecionarUsuario = async () => {
    const usuarioSelecionado = await showUsuarioSearch();

    if (usuarioSelecionado) {
      setUsuario(usuarioSelecionado);
    }
  };

  const buscarRelatorio = async () => {
    if (!dataIni || !dataFim || !horaIni || !horaFim) {
      return;
    }

    if (`${dataIni}T${horaIni}:00` > `${dataFim}T${horaFim}:59`) {
      showMessage('O período inicial não pode ser maior que o período final.', 'warning');
      return;
    }

    try {
      setBuscando(true);
      const resultado = await getRelatorioVendasGarcom({
        dataHoraIni: `${dataIni}T${horaIni}:00`,
        dataHoraFim: `${dataFim}T${horaFim}:59`,
        usuarioId: usuario?.id || 0,
      });

      setRelatorio({
        ...relatorioVazio,
        ...resultado,
        produtos: resultado?.produtos || [],
        pedidos: resultado?.pedidos || [],
        vendasPorGarcom: resultado?.vendasPorGarcom || [],
        totais: { ...relatorioVazio.totais, ...resultado?.totais },
      });
    } catch (error) {
      showMessage(`Erro ao buscar relatório de vendas por garçom: ${error}`, 'danger');
    } finally {
      setBuscando(false);
    }
  };

  useEffect(() => {
    buscarRelatorio();
  }, [dataIni, horaIni, dataFim, horaFim, usuario]);

  return (
    <PageContainer>
      <h2 className="page-title">Vendas Garçom</h2>

      <PageHeader>
        <div className={`group-div vendas-garcom-filtros${buscando ? ' vendas-garcom-filtros-buscando' : ''}`}>
          <Input
            label="Data Inicial"
            type="date"
            value={dataIni}
            onChange={(e) => setDataIni(e.target.value)}
            size={145}
          />
          <Input
            label="Hora Inicial"
            type="time"
            value={horaIni}
            onChange={(e) => setHoraIni(e.target.value)}
            size={105}
          />
          <Input
            label="Data Final"
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            size={145}
          />
          <Input
            label="Hora Final"
            type="time"
            value={horaFim}
            onChange={(e) => setHoraFim(e.target.value)}
            size={105}
          />
          <Input
            label="Usuário"
            value={usuario?.nome || ''}
            onChange={() => {}}
            placeholder="Selecione o usuário"
            btnBuscar={selecionarUsuario}
            size={260}
            disabled
          />
        </div>
      </PageHeader>

      <PageContent maxAltura="calc(100dvh - 235px)">
        <section className="vendas-garcom-grafico">
          <h3>Vendas por garçom</h3>
          <div className="vendas-garcom-grafico-conteudo">
            {vendasPorGarcom.length > 0 ? (
              <Bar data={graficoVendasGarcomData} options={graficoVendasGarcomOptions} />
            ) : (
              <div className="vendas-garcom-vazio vendas-garcom-grafico-vazio">
                Nenhuma venda encontrada para o período.
              </div>
            )}
          </div>
        </section>

        <div className="vendas-garcom-grids">
          <section className="vendas-garcom-grid">
            <h3>Vendas por produto</h3>
            <div className="vendas-garcom-grid-scroll">
              <table className="page-content-table vendas-garcom-table">
                <colgroup>
                  <col className="vendas-garcom-col-id" />
                  <col />
                  <col className="vendas-garcom-col-quantidade" />
                  <col className="vendas-garcom-col-valor" />
                </colgroup>
                <thead>
                  <tr>
                    <th>ProdutoID</th>
                    <th>Descrição</th>
                    <th style={{ textAlign: 'right' }}>Quantidade</th>
                    <th style={{ textAlign: 'right' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorio.produtos.map((produto) => (
                    <tr key={produto.produtoId}>
                      <td>{produto.produtoId}</td>
                      <td>{produto.descricaoProduto}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumeric(produto.quantidade, 0)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(produto.valorTotal)}</td>
                    </tr>
                  ))}
                  {relatorio.produtos.length === 0 && (
                    <tr>
                      <td colSpan={4} className="vendas-garcom-vazio">Nenhum produto encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="vendas-garcom-grid">
            <h3>Vendas por pedido</h3>
            <div className="vendas-garcom-grid-scroll">
              <table className="page-content-table vendas-garcom-table">
                <colgroup>
                  <col className="vendas-garcom-col-id" />
                  <col />
                  <col className="vendas-garcom-col-quantidade" />
                  <col className="vendas-garcom-col-valor" />
                </colgroup>
                <thead>
                  <tr>
                    <th>PedidoId</th>
                    <th>NomeCliente</th>
                    <th style={{ textAlign: 'right' }}>Quantidade</th>
                    <th style={{ textAlign: 'right' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorio.pedidos.map((pedido) => (
                    <tr key={pedido.pedidoId}>
                      <td>{pedido.pedidoId}</td>
                      <td>{pedido.nomeCliente || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumeric(pedido.quantidadeItens, 0)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(pedido.valorTotal)}</td>
                    </tr>
                  ))}
                  {relatorio.pedidos.length === 0 && (
                    <tr>
                      <td colSpan={4} className="vendas-garcom-vazio">Nenhum pedido encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </PageContent>

      <PageFooter>
        <div className="vendas-garcom-totais vendas-garcom-totais-esquerda">
          <span><strong>Itens:</strong> {totais.totalItens}</span>
          <span><strong>Quantidade:</strong> {formatNumeric(totais.quantidadeItens, 0)}</span>
        </div>
        <div className="vendas-garcom-totais vendas-garcom-totais-direita">
          <span><strong>Pedidos:</strong> {totais.quantidadePedidos}</span>
          <span><strong>Valor total:</strong> {formatCurrency(totais.valorTotal)}</span>
        </div>
      </PageFooter>
    </PageContainer>
  );
};

export default RelatorioVendasGarcomPage;
